import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, createBlock as _createBlock, toDisplayString as _toDisplayString, unref as _unref, resolveComponent as _resolveComponent, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700"
}
const _hoisted_2 = { class: "flex items-end gap-2" }
const _hoisted_3 = { class: "flex flex-wrap items-center justify-between gap-3" }
const _hoisted_4 = { class: "text-sm text-gray-700" }
const _hoisted_5 = {
  key: 1,
  class: "flex flex-wrap items-center gap-2"
}
const _hoisted_6 = {
  key: 0,
  class: "mt-2 text-sm text-gray-500"
}
const _hoisted_7 = { class: "flex flex-wrap items-center justify-between gap-3" }
const _hoisted_8 = { class: "text-sm text-gray-700" }
const _hoisted_9 = { class: "flex flex-wrap items-center justify-between gap-3" }
const _hoisted_10 = { class: "text-sm text-gray-700" }
const _hoisted_11 = {
  key: 2,
  class: "px-6 py-4 text-sm text-red-600",
  role: "alert"
}

import { computed, onMounted, ref } from 'vue'

import InputField from '@/components/neue/InputField.vue'
import NeueButton from '@/components/neue/NeueButton.vue'
import NeueToggle from '@/components/neue/NeueToggle.vue'
import SettingCard from '@/components/settings/SettingCard.vue'
import SettingRow from '@/components/settings/SettingRow.vue'
import { PARSLEY_PATH, runHeadline } from '@/helpers/parsley'
import { showLatestParsleyRun, trackParsleyRun, useParsleyRunTracker } from '@/helpers/parsleyRunTracker'
import { errorMessage, parsleyApi } from '@/services/parsleyApi'

const DESCRIPTION = 'Bring nutrition, ingredients and allergens in from your Parsley recipes.'
const KEY_HINT = 'Create an API key in your Parsley account and paste it here.'
const DISCONNECT_HINT =
  'The key is stored encrypted and is never shown again. To use a different key, disconnect and connect again.'
const DISCONNECT_NOTE =
  'Your links are kept. Products keep the data they last received and become editable again. When you reconnect, the next sync replaces any changes made to them in the meantime.'
const SYNC_HINT = 'Updates every linked product whose Parsley item changed.'
const PRODUCTS_HINT = 'Choose which Parsley item each product gets its nutrition from.'
const PANEL_HINT =
  "Adds Parsley's Nutrition Facts panel, with ingredients and allergens, as the last image on every linked product."
const PANEL_ON_NOTE = 'Panels are added by the next sync and refreshed when the Parsley item changes.'
const PANEL_OFF_NOTE = 'Your product images are left as they are.'
const PANEL_REMOVED_NOTE = 'Panels removed from your linked products.'
const PANEL_ERROR = "Couldn't save the nutrition panel setting. Please try again."
const LOST_NOTE = 'We lost touch with this run. Refresh the page to see how it ended.'
const LOAD_ERROR = "Couldn't load your Parsley connection. Please refresh the page."
const RUNS_ERROR = "Couldn't load the last sync. Please refresh the page."
const CONNECT_ERROR = "Couldn't connect to Parsley. Please try again."
const DISCONNECT_ERROR = "Couldn't disconnect Parsley. Please try again."
const SYNC_ERROR = "Couldn't start the sync. Please try again."


export default {
  __name: 'ParsleyConnectionCard',
  setup(__props) {

const connection = ref(null)
const apiKey = ref('')
const error = ref('')
const busy = ref(false)
const confirmingDisconnect = ref(false)

const { run, running, lost, clear } = useParsleyRunTracker()

const connected = computed(() => Boolean(connection.value?.attributes?.turned_on))
const last4 = computed(() => connection.value?.attributes?.api_key_last4 ?? '')
const headline = computed(() => runHeadline(run.value))
const panelOn = computed(() => Boolean(connection.value?.attributes?.attach_nutrition_panel))
const panelJustRemoved = ref(false)
const panelNote = computed(() => {
  if (panelOn.value) return PANEL_ON_NOTE
  return panelJustRemoved.value ? PANEL_REMOVED_NOTE : PANEL_OFF_NOTE
})

// Not loading the last run is not a failure to load the connection, so the
// card still shows as connected.
const showLatestRun = async () => {
  try {
    await showLatestParsleyRun({ fetchRuns: parsleyApi.runs, fetchRun: parsleyApi.run })
  } catch (failure) {
    error.value = errorMessage(failure, RUNS_ERROR)
  }
}

const load = async () => {
  try {
    connection.value = await parsleyApi.connection()
  } catch (failure) {
    error.value = errorMessage(failure, LOAD_ERROR)
    return
  }
  if (connected.value) await showLatestRun()
}

const connect = async () => {
  const key = apiKey.value.trim()
  if (!key || busy.value) return
  busy.value = true
  error.value = ''
  try {
    connection.value = await parsleyApi.connect(key)
    // The key has been sent. Nothing on the page needs it again.
    apiKey.value = ''
  } catch (failure) {
    error.value = errorMessage(failure, CONNECT_ERROR)
    return
  } finally {
    busy.value = false
  }
  if (connected.value) await showLatestRun()
}

const disconnect = async () => {
  if (busy.value) return
  busy.value = true
  error.value = ''
  try {
    connection.value = await parsleyApi.disconnect()
    confirmingDisconnect.value = false
    clear()
  } catch (failure) {
    error.value = errorMessage(failure, DISCONNECT_ERROR)
  } finally {
    busy.value = false
  }
}

const setNutritionPanel = async on => {
  if (busy.value) return
  busy.value = true
  error.value = ''
  try {
    connection.value = await parsleyApi.setNutritionPanel(on)
    panelJustRemoved.value = !on
  } catch (failure) {
    error.value = errorMessage(failure, PANEL_ERROR)
  } finally {
    busy.value = false
  }
}

const sync = async () => {
  if (running.value) return
  error.value = ''
  try {
    trackParsleyRun({ run: await parsleyApi.startSync(), fetchRun: parsleyApi.run })
  } catch (failure) {
    error.value = errorMessage(failure, SYNC_ERROR)
  }
}

onMounted(load)

return (_ctx, _cache) => {
  const _component_router_link = _resolveComponent("router-link")

  return (_openBlock(), _createBlock(SettingCard, {
    name: "Parsley",
    description: DESCRIPTION
  }, {
    action: _withCtx(() => [
      (connected.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_1, " Connected "))
        : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      (!connected.value)
        ? (_openBlock(), _createBlock(SettingRow, {
            key: 0,
            label: "API key",
            hint: KEY_HINT
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(InputField, {
                  modelValue: apiKey.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((apiKey).value = $event)),
                  type: "password",
                  placeholder: "Paste your Parsley API key",
                  autocomplete: "new-password",
                  autocorrect: "off",
                  autocapitalize: "off",
                  spellcheck: "false",
                  "data-1p-ignore": "",
                  "data-lpignore": "true",
                  class: "flex-1",
                  onKeyup: _withKeys(connect, ["enter"])
                }, null, 8, ["modelValue"]),
                _createVNode(NeueButton, {
                  type: apiKey.value.trim() ? 'primary' : 'inactive',
                  loading: busy.value,
                  onClick: connect
                }, {
                  default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                    _createTextVNode("Connect", -1)
                  ]))]),
                  _: 1
                }, 8, ["type", "loading"])
              ])
            ]),
            _: 1
          }))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createVNode(SettingRow, {
              label: "API key",
              hint: DISCONNECT_HINT
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("span", _hoisted_4, "Ending in " + _toDisplayString(last4.value), 1),
                  (!confirmingDisconnect.value)
                    ? (_openBlock(), _createBlock(NeueButton, {
                        key: 0,
                        type: "secondary",
                        onClick: _cache[1] || (_cache[1] = $event => (confirmingDisconnect.value = true))
                      }, {
                        default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                          _createTextVNode(" Disconnect ", -1)
                        ]))]),
                        _: 1
                      }))
                    : (_openBlock(), _createElementBlock("span", _hoisted_5, [
                        _createVNode(NeueButton, {
                          type: "secondary",
                          onClick: _cache[2] || (_cache[2] = $event => (confirmingDisconnect.value = false))
                        }, {
                          default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                            _createTextVNode("Keep connected", -1)
                          ]))]),
                          _: 1
                        }),
                        _createVNode(NeueButton, {
                          type: "red",
                          loading: busy.value,
                          onClick: disconnect
                        }, {
                          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                            _createTextVNode("Disconnect Parsley", -1)
                          ]))]),
                          _: 1
                        }, 8, ["loading"])
                      ]))
                ]),
                (confirmingDisconnect.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(DISCONNECT_NOTE)))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            _createVNode(SettingRow, {
              label: "Sync",
              hint: SYNC_HINT
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(lost) ? LOST_NOTE : headline.value), 1),
                  _createVNode(NeueButton, {
                    type: "secondary",
                    loading: _unref(running),
                    onClick: sync
                  }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode("Sync now", -1)
                    ]))]),
                    _: 1
                  }, 8, ["loading"])
                ])
              ]),
              _: 1
            }),
            _createVNode(SettingRow, {
              label: "Products",
              hint: PRODUCTS_HINT
            }, {
              default: _withCtx(() => [
                _createVNode(_component_router_link, {
                  to: _unref(PARSLEY_PATH),
                  class: "text-sm font-medium text-brand-600 hover:text-brand-700"
                }, {
                  default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                    _createTextVNode(" Link products to Parsley ", -1)
                  ]))]),
                  _: 1
                }, 8, ["to"])
              ]),
              _: 1
            }),
            _createVNode(SettingRow, {
              label: "Nutrition panel image",
              hint: PANEL_HINT
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("span", _hoisted_10, _toDisplayString(panelNote.value), 1),
                  _createVNode(NeueToggle, {
                    "model-value": panelOn.value,
                    disabled: busy.value,
                    compact: "",
                    onChange: setNutritionPanel
                  }, null, 8, ["model-value", "disabled"])
                ])
              ]),
              _: 1
            })
          ], 64)),
      (error.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(error.value), 1))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

}